/**
 * VideoConfigureStep - Configure blur settings for video files
 *
 * Step 2 in the video workflow (5-step process):
 * Upload -> Trim -> Configure -> Preview -> Download
 *
 * This component handles:
 * - Starting face detection (on first render if no detections yet)
 * - Showing detection progress
 * - Person configuration (blur settings after detection completes)
 */
import React, { useState, useEffect, useCallback } from 'react';
import VideoProgressBar from './VideoProgressBar';
import { useVideoProgress, useVideoDetection } from '../hooks/useVideoProgress';
import { videoAPI, formatDuration } from '../utils/videoUtils';
import { API_URL } from '../utils/constants';

/**
 * PersonCard - Individual person configuration card
 */
function PersonCard({ person, settings, onToggle, onSettingChange }) {
  const isEnabled = settings?.enabled ?? false;

  return (
    <div className={`bg-gray-800/50 rounded-lg p-4 border transition-all ${
      isEnabled ? 'border-emerald-500/50' : 'border-gray-700'
    }`}>
      {/* Header with thumbnail and toggle */}
      <div className="flex items-center gap-3 mb-3">
        {/* Thumbnail */}
        <div className="relative">
          {person.thumbnail_url ? (
            <img
              src={person.thumbnail_url}
              alt={person.person_id}
              className="w-12 h-12 rounded-lg object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
          {/* Gender badge */}
          {person.gender && (
            <span className={`absolute -bottom-1 -right-1 text-xs px-1.5 py-0.5 rounded-full font-medium ${
              person.gender === 'male' ? 'bg-blue-500/80 text-blue-100' : 'bg-pink-500/80 text-pink-100'
            }`}>
              {person.gender === 'male' ? '♂' : '♀'}
            </span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium capitalize">
            {person.person_id.replace('_', ' ')}
          </h4>
          <p className="text-xs text-gray-400">
            {person.gender ? `${person.gender} • ` : ''}
            {person.frame_count} frames
          </p>
        </div>

        {/* Toggle */}
        <button
          onClick={() => onToggle(person.person_id)}
          className={`w-12 h-6 rounded-full transition-colors relative ${
            isEnabled ? 'bg-emerald-500' : 'bg-gray-600'
          }`}
        >
          <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
            isEnabled ? 'translate-x-7' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Settings (only shown when enabled) */}
      {isEnabled && (
        <div className="space-y-3 pt-3 border-t border-gray-700/50">
          {/* Blur Level */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Blur Level</label>
            <div className="flex gap-2">
              {['low', 'high', 'extreme'].map(level => (
                <button
                  key={level}
                  onClick={() => onSettingChange(person.person_id, 'level', level)}
                  className={`flex-1 py-1.5 px-2 text-xs rounded-lg transition-colors ${
                    settings?.level === level
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Blur Target */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Target</label>
            <div className="flex gap-2">
              {['face', 'body', 'both'].map(target => (
                <button
                  key={target}
                  onClick={() => onSettingChange(person.person_id, 'target', target)}
                  className={`flex-1 py-1.5 px-2 text-xs rounded-lg transition-colors ${
                    settings?.target === target
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {target.charAt(0).toUpperCase() + target.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * VideoConfigureStep Component
 */
export default function VideoConfigureStep({
  file,
  fileIndex,
  onFileChange,
  onShowToast,
  bulkGender,
  onBulkGenderChange,
  bulkLevel,
  onBulkLevelChange,
  bulkTarget,
  onBulkTargetChange,
}) {
  // Detection/Configure state
  const [detectionComplete, setDetectionComplete] = useState(false);
  const [detectionStarted, setDetectionStarted] = useState(false);
  const [persons, setPersons] = useState([]);

  // Get trim range from file (set in VideoTrimStep)
  const trimStart = file?.trimRange?.start || 0;
  const trimEnd = file?.trimRange?.end || (file?.maxBlurDuration || 60);

  const { jobId, isStarting, startDetection } = useVideoDetection();

  // Get max blur duration from file metadata (set by backend)
  const maxBlurDuration = file?.maxBlurDuration || 60;
  const videoDuration = file?.metadata?.duration_seconds || 0;

  // Progress tracking
  const {
    progress,
    eta,
    step,
    status,
    error,
    result,
    isProcessing,
    isComplete,
    isFailed,
  } = useVideoProgress(jobId, {
    jobType: 'detection', // Use detection endpoint to get persons array
    onComplete: (data) => {
      setDetectionComplete(true);
      if (data.persons) {
        // Fix thumbnail URLs by prepending API_URL if they're relative
        const personsWithFullUrls = data.persons.map(p => ({
          ...p,
          thumbnail_url: p.thumbnail_url?.startsWith('/')
            ? `${API_URL}${p.thumbnail_url}`
            : p.thumbnail_url
        }));
        setPersons(personsWithFullUrls);
        // Initialize blur settings for each person
        const initialSettings = {};
        personsWithFullUrls.forEach(p => {
          initialSettings[p.person_id] = {
            enabled: false,
            level: 'high',
            target: 'face'
          };
        });
        onFileChange(prev => ({
          ...prev,
          detections: personsWithFullUrls,
          blurSettings: initialSettings
        }));
      }
      onShowToast?.(`Detected ${data.persons?.length || 0} persons in video`, 'success');
    },
    onError: (msg) => {
      onShowToast?.(`Detection failed: ${msg}`, 'error');
    },
  });

  // Auto-start detection when entering this step (if not already detected)
  useEffect(() => {
    if (file?.sessionId && file?.fileId && !detectionStarted && !file?.detections?.length) {
      setDetectionStarted(true);
      startDetection(file.sessionId, file.fileId, true, trimStart, trimEnd)
        .catch((err) => {
          onShowToast?.(`Detection failed: ${err.message}`, 'error');
        });
    }
  }, [file?.sessionId, file?.fileId, detectionStarted, file?.detections?.length, startDetection, trimStart, trimEnd, onShowToast]);

  // Check if file already has detections (coming back from preview)
  useEffect(() => {
    if (file?.detections?.length) {
      setDetectionComplete(true);
      setPersons(file.detections);
    }
  }, [file?.detections]);

  // Toggle person blur
  const togglePerson = useCallback((personId) => {
    onFileChange(prev => {
      const settings = prev.blurSettings?.[personId] || { enabled: false, level: 'high', target: 'face' };
      return {
        ...prev,
        blurSettings: {
          ...prev.blurSettings,
          [personId]: { ...settings, enabled: !settings.enabled }
        }
      };
    });
  }, [onFileChange]);

  // Update person setting
  const updatePersonSetting = useCallback((personId, key, value) => {
    onFileChange(prev => {
      const settings = prev.blurSettings?.[personId] || { enabled: true, level: 'high', target: 'face' };
      return {
        ...prev,
        blurSettings: {
          ...prev.blurSettings,
          [personId]: { ...settings, [key]: value }
        }
      };
    });
  }, [onFileChange]);

  // Apply bulk settings
  const applyBulkSettings = useCallback(() => {
    if (!bulkGender) return;

    const targetPersons = bulkGender === 'all'
      ? persons
      : persons.filter(p => p.gender === bulkGender);

    if (targetPersons.length === 0) {
      onShowToast?.(`No ${bulkGender === 'all' ? 'persons' : bulkGender + 's'} detected`, 'error');
      return;
    }

    const newSettings = {};
    persons.forEach(p => {
      const matches = bulkGender === 'all' || p.gender === bulkGender;
      newSettings[p.person_id] = {
        enabled: matches,
        level: bulkLevel,
        target: bulkTarget
      };
    });

    onFileChange(prev => ({
      ...prev,
      blurSettings: newSettings
    }));

    onShowToast?.(`Applied blur to ${targetPersons.length} person(s)`, 'success');
  }, [bulkGender, bulkLevel, bulkTarget, persons, onFileChange, onShowToast]);

  // Count enabled persons
  const enabledCount = Object.values(file?.blurSettings || {}).filter(s => s.enabled).length;

  // Show progress bar during detection
  if (isProcessing || isStarting) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        {/* Video Info */}
        <div className="bg-gray-800/50 rounded-xl p-4 mb-6 border border-gray-700">
          <div className="flex items-center gap-4">
            {file?.thumbnail && (
              <img
                src={file.thumbnail}
                alt={file.name}
                className="w-24 h-16 object-cover rounded-lg"
              />
            )}
            <div>
              <h3 className="text-white font-medium">{file?.name}</h3>
              <p className="text-sm text-gray-400">
                {file?.metadata?.width}x{file?.metadata?.height} •{' '}
                Processing {formatDuration(trimStart)} - {formatDuration(trimEnd)} •{' '}
                {file?.metadata?.fps?.toFixed(0)} fps
              </p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <VideoProgressBar
          progress={progress}
          eta={eta}
          step={step}
          status={status}
          title="Detecting Faces"
        />
      </div>
    );
  }

  // Show error state
  if (isFailed) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-red-900/20 rounded-xl p-6 border border-red-500/50 text-center">
          <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-xl font-medium text-red-400 mb-2">Detection Failed</h3>
          <p className="text-gray-400">{error}</p>
          <button
            onClick={() => startDetection(file.sessionId, file.fileId)}
            className="mt-4 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Retry Detection
          </button>
        </div>
      </div>
    );
  }

  // Main configuration UI
  return (
    <div className="max-w-4xl mx-auto px-4">
      {/* Video Info Header */}
      <div className="bg-gray-800/50 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {file?.thumbnail && (
              <img
                src={file.thumbnail}
                alt={file.name}
                className="w-20 h-14 object-cover rounded-lg"
              />
            )}
            <div>
              <h3 className="text-white font-medium">{file?.name}</h3>
              <p className="text-sm text-gray-400">
                {formatDuration(file?.trimRange ? (file.trimRange.end - file.trimRange.start) : file?.metadata?.duration_seconds)} •{' '}
                {persons.length} person(s) detected
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-emerald-400">{enabledCount}</p>
            <p className="text-xs text-gray-400">to blur</p>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="bg-gray-800/50 rounded-xl p-4 mb-6 border border-gray-700">
        <h3 className="text-white font-medium mb-4">Quick Apply</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Gender Filter */}
          <div>
            <label className="text-xs text-gray-400 mb-2 block">Apply to</label>
            <div className="flex gap-2">
              {[
                { value: 'all', label: 'All' },
                { value: 'male', label: '♂ Males' },
                { value: 'female', label: '♀ Females' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => onBulkGenderChange(opt.value)}
                  className={`flex-1 py-2 px-3 text-sm rounded-lg transition-colors ${
                    bulkGender === opt.value
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Blur Level */}
          <div>
            <label className="text-xs text-gray-400 mb-2 block">Blur Level</label>
            <select
              value={bulkLevel}
              onChange={e => onBulkLevelChange(e.target.value)}
              className="w-full py-2 px-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-emerald-500"
            >
              <option value="low">Low</option>
              <option value="high">High</option>
              <option value="extreme">Extreme</option>
            </select>
          </div>

          {/* Target */}
          <div>
            <label className="text-xs text-gray-400 mb-2 block">Target</label>
            <select
              value={bulkTarget}
              onChange={e => onBulkTargetChange(e.target.value)}
              className="w-full py-2 px-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-emerald-500"
            >
              <option value="face">Face Only</option>
              <option value="body">Body Only</option>
              <option value="both">Both</option>
            </select>
          </div>

          {/* Apply Button */}
          <div className="flex items-end">
            <button
              onClick={applyBulkSettings}
              disabled={!bulkGender}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                bulkGender
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Person Cards */}
      {persons.length > 0 ? (
        <div className="space-y-4">
          <h3 className="text-white font-medium flex items-center justify-between">
            <span>Detected Persons</span>
            <span className="text-sm text-gray-400">{persons.length} found</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {persons.map(person => (
              <PersonCard
                key={person.person_id}
                person={person}
                settings={file?.blurSettings?.[person.person_id]}
                onToggle={togglePerson}
                onSettingChange={updatePersonSetting}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700 text-center">
          <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="text-xl font-medium text-gray-300 mb-2">No Persons Detected</h3>
          <p className="text-gray-500">No faces or bodies were found in this video.</p>
        </div>
      )}

      {/* Summary */}
      {enabledCount > 0 && (
        <div className="mt-6 bg-emerald-900/20 rounded-xl p-4 border border-emerald-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-emerald-100">
                Ready to blur <strong>{enabledCount}</strong> person(s) in this video
              </span>
            </div>
            <span className="text-emerald-400 text-sm">
              Click "Continue" to preview
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
